import { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { agentsApi } from '../utils/apiClient';

interface Agent {
  id: string;
  email: string;
  name: string;
  isAdmin: boolean;
  isActive: boolean;
  status: string;
  createdAt: string;
  updatedAt: string;
}

interface AgentsMeta {
  page: number;
  limit: number;
  total: number;
}

function AgentsPage() {
  const [agents, setAgents] = useState<Agent[]>([]);
  const [meta, setMeta] = useState<AgentsMeta | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchAgents = async () => {
    setLoading(true);
    setError('');

    const response = await agentsApi.list();
    setLoading(false);

    if (!response.success) {
      setError(response.error);
      toast.error(response.error);
      return;
    }

    const data = response.data.data as Agent[] | undefined;
    const metaData = response.data.meta as AgentsMeta | undefined;
    setAgents(data ?? []);
    setMeta(metaData ?? null);
  };

  useEffect(() => {
    fetchAgents();
  }, []);

  return (
    <div className="h-full min-h-0 flex flex-col space-y-6">
      <div className="rounded-3xl border border-emerald-100 bg-white/70 p-6 flex-1 min-h-0 flex flex-col">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <p className="text-xs uppercase tracking-[0.4em] text-emerald-500">Agents</p>
            <h2 className="text-xl font-semibold text-slate-900">Agents list</h2>
          </div>
          {meta && (
            <p className="text-xs uppercase tracking-[0.4em] text-slate-400">
              Showing {agents.length} of {meta.total}
            </p>
          )}
        </div>
        {loading ? (
          <p className="text-sm text-slate-500">Loading agents…</p>
        ) : error ? (
          <p className="text-sm text-rose-600">{error}</p>
        ) : (
          <ul className="space-y-3 overflow-y-auto pr-2">
            {agents.map((agent) => (
              <li
                key={agent.id}
                className="flex flex-col gap-1 rounded-2xl border border-slate-100 bg-slate-50 px-4 py-3 shadow-sm shadow-slate-200/70"
              >
                <div className="flex items-center justify-between text-sm uppercase tracking-[0.3em] text-slate-400">
                  <span>{agent.status}</span>
                  <span className="text-xs font-semibold text-emerald-600">
                    Active: {agent.isActive ? 'Yes' : 'No'}
                  </span>
                </div>
                <p className="text-base font-semibold text-slate-900">{agent.name}</p>
                <p className="text-sm text-slate-600">{agent.email}</p>
                <p className="text-xs text-slate-500">
                  Created {new Date(agent.createdAt).toLocaleDateString()}
                </p>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

export default AgentsPage;
